import { fn } from '@ember/helper';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import type Owner from '@ember/owner';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismCheckbox, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import type { RiBreakdown } from 'frontend-utils/utils/constants/constant-values';
import cloneDeep from 'lodash.clonedeep';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import GeneralModalsModalSort from 'lumen/components/general/modals/modal-sort';
import type AjaxService from 'lumen/services/ajax';
import {
  CANCELLATION_ONLY_BLOCKS,
  MAPPING_BLOCKS,
  MAX_AMOUNT_OF_BLOCKS,
  TARGET_BLOCKS,
} from 'lumen/utils/constants/ri-constant-values';

export interface SegmentationBlock {
  isMappingVisible: boolean;
  isSupported?: boolean;
  isVisible: boolean;
  segmentationKey: RiBreakdown;
  translationKey: string;
  userSegmentationOrderIndex?: number;
}

interface HotelAndMarketRevenueInsightSortAndReorderBlocksSignature {
  Args: {
    closeModal: () => unknown;
    doRefresh: () => unknown;
    segmentationBlocksOrder: SegmentationBlock[];
  };
}

export default class HotelAndMarketRevenueInsightSortAndReorderBlocks extends Component<HotelAndMarketRevenueInsightSortAndReorderBlocksSignature> {
  @service declare ajax: AjaxService;
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isLoading = false;
  @tracked supportedSegmentationBlocksOrder: SegmentationBlock[] | null = null;
  // order need to be saved, so we put all changes in this temp variable
  @tracked tempSegmentationBlocksOrder: SegmentationBlock[] | null = null;

  actionNames;

  /**
   * Need to calculate the position of each segmentation block beforehand because we need to take the visibility into account
   * Also add a state for the hover
   * @return { array } returns the sementationBlocksOrder array maaped with the new state
   */
  @computed(
    'maxNumberOfVisibleBlocksReached',
    'numberOfVisibleBlocks',
    'tempSegmentationBlocksOrder.@each.{isMappingVisible,isVisible}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get displayTempSegmentationBlocks() {
    if (!this.tempSegmentationBlocksOrder) {
      return [];
    }

    // start at 0, add if visible
    let position = 0;
    const hasOnlyOneVisibleBlock = this.numberOfVisibleBlocks === 1;
    return this.tempSegmentationBlocksOrder.map((block) => {
      const isLastVisibleBlock = block.isVisible && hasOnlyOneVisibleBlock;
      const isDisabled =
        !block.isVisible && this.maxNumberOfVisibleBlocksReached; // Only non-checked blocks can be fully disabled
      const isDisabledForTarget =
        (TARGET_BLOCKS as string[]).indexOf(block.segmentationKey) === -1;
      const isDisabledForBookings =
        (CANCELLATION_ONLY_BLOCKS as string[]).indexOf(
          block.segmentationKey,
        ) !== -1;
      const isEnabledForMapping =
        (MAPPING_BLOCKS as string[]).indexOf(block.segmentationKey) !== -1;

      // Add a position number to the list item it checkbox is checked and increment the position counter
      let blockPosition = null;
      if (block.isVisible) {
        position += 1;
        blockPosition = position;
      }

      return {
        segmentationKey: block.segmentationKey,
        translationKey: block.translationKey,
        isVisible: block.isVisible,
        isLastVisibleBlock,
        isDisabled,
        isMappingVisible: block.isMappingVisible,
        isSupported: block.isSupported,
        isHovering: false,
        isDisabledForTarget,
        isEnabledForMapping,
        position: blockPosition,
        isDisabledForBookings,
      };
    });
  }

  /**
   * If any mapping visibility changed, this property will return true
   * @return { boolean }
   */
  @computed(
    'tempSegmentationBlocksOrder.@each.{segmentationKey,isMappingVisible}',
    'supportedSegmentationBlocksOrder.@each.{segmentationKey,isMappingVisible}',
  )
  get mappingVisibilityChanged(): boolean {
    // For every segmentation block, check if the corresponding temp version has a changed mapping visibility.
    // For this check the order does not matter.
    const visibilityChanged = this.tempSegmentationBlocksOrder?.some(
      (tempBlock) =>
        this.supportedSegmentationBlocksOrder?.some(
          (block) =>
            block.segmentationKey === tempBlock.segmentationKey &&
            block.isMappingVisible !== tempBlock.isMappingVisible,
        ),
    );

    return Boolean(visibilityChanged);
  }

  // Only for RI Multi Property
  @dependentKeyCompat
  get maxNumberOfVisibleBlocksReached(): boolean {
    return (
      this.riSelections.isMultiProperty &&
      this.numberOfVisibleBlocks >= MAX_AMOUNT_OF_BLOCKS
    );
  }

  /**
   * Number of blocks that are selected. This will be used to determine if we can uncheck a box or not
   * 1 should always be checked
   * @return { int } : the number of visible blocks
   */
  @computed('tempSegmentationBlocksOrder.@each.isVisible')
  get numberOfVisibleBlocks(): number {
    if (!this.tempSegmentationBlocksOrder) {
      return 0;
    }

    return this.tempSegmentationBlocksOrder.reduce((total, currentBlock) => {
      if (currentBlock.isVisible) {
        return total + 1;
      }
      return total;
    }, 0);
  }

  /**
   * If any visibilty or order has changed in the tempSegmentationBlocksOrder array, this property will return true
   * @return { boolean }
   */
  @computed(
    'tempSegmentationBlocksOrder.@each.{isVisible,segmentationKey}',
    'supportedSegmentationBlocksOrder.@each.{isVisible,segmentationKey}',
    'mappingVisibilityChanged',
  )
  get orderOrVisibilityChanged(): boolean {
    return (
      this.mappingVisibilityChanged ||
      Boolean(
        this.tempSegmentationBlocksOrder?.some(
          (block, index) =>
            block.isVisible !==
              this.supportedSegmentationBlocksOrder?.[index]?.isVisible ||
            block.segmentationKey !==
              this.supportedSegmentationBlocksOrder?.[index]?.segmentationKey,
        ),
      )
    );
  }

  constructor(
    owner: Owner,
    args: HotelAndMarketRevenueInsightSortAndReorderBlocksSignature['Args'],
  ) {
    super(owner, args);

    this.actionNames = [
      this.intl.t('revenue_insight.breakdown_settings.target_comparisons'),
      this.intl.t('revenue_insight.breakdown_settings.use_hierarchy'),
    ];

    this.didReceiveAttrsBlocks(null, [this.args.segmentationBlocksOrder]);
  }

  /**
   * Merges in the old state of segmentation blocks which are not supported in the new state.
   *
   * Problem: When making changes to the breakdown settings you don't want options for other PMSs to dissapear.
   * Solution: Here we take a best effor approach to merging in the old, unsupported blocks in the new state.
   *           The merging starts from the new state and tries to insert blocks from the old state at the same index.
   *
   * Example:
   *           Old blocks: [A, B, C, D]
   *           New blocks: [C, B] (With B,C,D supported in the current PMS)
   *           Becomes: [A, C, B] (A is merged in because it's not supported in the current PMS, D is dropped because it's set to invisible)
   *
   * @param {array} oldBlocks : The original order and state of all the segmentation blocks before editing.
   * @param {array} newBlocks : The new order and state after editing the segmentation blocks in this modal.
   * @return {array} blocks : The merged order and state of both inputs.
   */
  mergeSupportedSegmentationBlocksOrder(
    oldBlocks: SegmentationBlock[],
    newBlocks: SegmentationBlock[],
  ): SegmentationBlock[] {
    const blocks = cloneDeep(newBlocks);
    oldBlocks.forEach((oldBlock, i) => {
      if (oldBlock.isVisible && !oldBlock.isSupported) {
        const insertIndex = Math.min(i, blocks.length);
        blocks.splice(insertIndex, 0, cloneDeep(oldBlock));
      }
    });

    return blocks;
  }

  /**
   * Reset the original order and visibility of the segmentation blocks
   * Closes the modal afterwards
   */
  @action
  cancelReorderBlocks(): void {
    this.tempSegmentationBlocksOrder = cloneDeep(
      this.supportedSegmentationBlocksOrder,
    );
    this.args.closeModal();
  }

  @action
  didReceiveAttrsBlocks(
    _element: unknown,
    [segmentationBlocksOrder]: [SegmentationBlock[]],
  ): void {
    // reset tempSegmentationBlocksOrder with the passed arguments
    // this should always be kept in sync since the segmentationBlocksOrder is the 'ground truth' of the order and visibility
    this.supportedSegmentationBlocksOrder = segmentationBlocksOrder.filter(
      (block) => block.isSupported,
    );

    this.tempSegmentationBlocksOrder = cloneDeep(
      this.supportedSegmentationBlocksOrder,
    );
  }

  /**
   * Sets the new order of the blocks
   * This is done by mapping the blocks to the format used in the controller
   * We do not nedd the extra properties we added for display purposes
   * @param  { array } groupModel : An array with the order of the segmentation blocks
   */
  @action
  reorderBlocks(groupModel: SegmentationBlock[]): void {
    this.tempSegmentationBlocksOrder = groupModel.map((block) => {
      return {
        segmentationKey: block.segmentationKey,
        translationKey: block.translationKey,
        isVisible: block.isVisible,
        isMappingVisible: block.isMappingVisible,
      };
    });
  }

  /**
   * Saves the temporary order and visibility
   * Closes the modal afterwards
   */
  @action
  saveReorderBlocks(): void {
    if (!this.tempSegmentationBlocksOrder) {
      return;
    }

    // Save all temp blocks + keep the values for the ones which are not configurable for this sub
    const blocks = this.mergeSupportedSegmentationBlocksOrder(
      this.args.segmentationBlocksOrder,
      this.tempSegmentationBlocksOrder,
    );
    if (blocks) {
      this.usageAnalytics.track('Settings Changed', {
        eventTrigger: 'breakdown-display-settings',
        settingsName: 'breakdown-display',
      });

      this.isLoading = true;
      // The API expects an array of segmentationKeys. Only those that are visible in order
      const segmentations = blocks
        .filter((block) => block.isVisible)
        .map((block) => block.segmentationKey);
      const data = {
        user: {} as Record<string, unknown>, // keys depend on whether it's multi or single property
      };

      if (this.riSelections.isMultiProperty) {
        data.user['ri_mp_segmentations'] = segmentations;
      } else {
        data.user['segmentations'] = segmentations;
      }

      const isMappingVisibilityChanged = this.mappingVisibilityChanged;
      if (isMappingVisibilityChanged) {
        const mappingVisibilities = blocks
          .filter((block) => block.isMappingVisible)
          .map((block) => block.segmentationKey);
        if (this.riSelections.isMultiProperty) {
          data.user['ri_mp_category_mappings'] = mappingVisibilities;
        } else {
          data.user['category_mappings'] = mappingVisibilities;
        }
      }
      this.ajax
        .patch('/user/segmentations/', {
          data: JSON.stringify(data),
          contentType: 'application/json',
        })
        .then((payload) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          this.store.pushPayload(payload);
          this.isLoading = false;
          this.args.closeModal();

          if (isMappingVisibilityChanged || this.riSelections.isMultiProperty) {
            this.args.doRefresh();
          }
        });
    } else {
      this.args.closeModal();
    }
  }

  @action
  toggleMappingVisibility(index: number): void {
    const block = this.tempSegmentationBlocksOrder?.[index];

    if (block) {
      set(block, 'isMappingVisible', !block.isMappingVisible);
    }
  }

  /**
   * Toggle the visibility of a certain segmentation block
   * @param  {[type]} index : index of the item where the visibility should
   * @return {[type]}       [description]
   */
  @action
  toggleVisibility(index: number): void {
    const block = this.tempSegmentationBlocksOrder?.[index];

    if (block) {
      set(block, 'isVisible', !block.isVisible);
    }
  }

  <template>
    <div {{didUpdate this.didReceiveAttrsBlocks @segmentationBlocksOrder}}>
      <GeneralModalsModalSort
        @actionNames={{this.actionNames}}
        @canToggleVisibility={{true}}
        @items={{this.displayTempSegmentationBlocks}}
        @name={{t "revenue_insight.headers.segments"}}
        @onCancel={{this.cancelReorderBlocks}}
        @onReorder={{this.reorderBlocks}}
        @onSave={{this.saveReorderBlocks}}
        @saveDisabled={{not this.orderOrVisibilityChanged}}
        as |item index|
      >
        <div
          class="breakdown-display-item flex flex-row
            {{if item.isHovering 'breakdown-display-item--highlighted'}}"
        >
          <div class="handle-container">
            <div class="handle flex items-center justify-center">
              <PrismIcon @name="drag" />
            </div>
            <div
              class="position truncate
                {{unless item.isDisabled 'highlight-value'}}"
            >
              {{#if item.position}}
                {{item.position}}
              {{else}}
                {{t "general.unavailable"}}
              {{/if}}
            </div>
            <div class="visibility-toggle flex items-center justify-center">
              <PrismCheckbox
                @checked={{item.isVisible}}
                @disabled={{or item.isLastVisibleBlock item.isDisabled}}
                @onChange={{fn this.toggleVisibility index}}
              />
            </div>
            <div
              class="item-name flex items-center gap-200 truncate
                {{unless item.isDisabled 'highlight-value'}}"
            >
              {{t item.translationKey}}
              {{#if item.isDisabledForBookings}}
                <GeneralIconWithTooltip
                  @color="warning"
                  @iconName="warning"
                  @side="top"
                  @size="small"
                  @tooltipContent={{t
                    "revenue_insight.cancellations.breakdown_not_available"
                  }}
                />
              {{/if}}
            </div>
          </div>
          <div class="action-container">
            {{#if item.isDisabledForTarget}}
              {{t "general.unavailable"}}
            {{else}}
              <PrismIcon @color="success" @name="circle-checkmark" />
            {{/if}}
          </div>
          <div class="action-container">
            {{#if item.isEnabledForMapping}}
              <PrismCheckbox
                @checked={{item.isMappingVisible}}
                @disabled={{item.isDisabled}}
                @onChange={{fn this.toggleMappingVisibility index}}
              />
            {{else}}
              {{t "general.unavailable"}}
            {{/if}}
          </div>
        </div>
      </GeneralModalsModalSort>
    </div>
  </template>
}
